import React from 'react'
import { isBrowser } from "react-device-detect"

import SiteNameHoverPopup from './SiteNameHoverPopup'

import {
  MarkerWrapper,
  MarkerDiv,
  MarkerDivNominatedOuter,
} from './styled-elements'

import WindArrow from '../icons/wind-arrow.svg'
import OctagonMarker from '../icons/OctagonMarker'
import HexagonMarker from "../icons/HexagonMarker";
import { isDataFresh } from "../../utils/mapUtils";
import PentagonMarker from "../icons/PentagonMarker";
import ArrowMarker from "../icons/ArrowMarker";

function computeZIndex(awsType) {
  // Site type layer positions.
  // Sites types closer to the start of the list appear on top of other sites.
  // If a site type is not in this list it appears on top.
  const order = ['standard', 'research', 'hilltop', 'bom', 'greenbrain'];
  const pos = order.indexOf(awsType);
  return 120 - (pos + 1);
}

function computeWindDirection(windDirection) {
  if (windDirection === undefined || windDirection == null) return null;
  if (windDirection === "C") return "C";
  return windDirection <= 180 ? windDirection + 180 : windDirection - 180;
}

function computeStyles({ awsType, bgColor, fontColor, windDirection }) {
  if (['bom', 'greenbrain', 'hilltop'].includes(awsType)) {
    return {
      color: fontColor || "#000",
      noWindFadedOutlineColor: windDirection ? null : "rgba(0, 0, 0, 0.25)",
    };
  } else {
    return {
      borderColor: windDirection ? "black" : "rgba(0, 0, 0, 0.25)",
      backgroundColor: bgColor || "#FFF",
      color: fontColor || "#000",
    };
  }
}

export function mapWindSpeedArrowMode({ showWindArrows, metricKey }) {
  return showWindArrows && ['wind_speed_2m', 'wind_speed_10m'].includes(metricKey);
}

function GreenbrainMarker(props) {
  return (
    <MarkerWrapper zIndexLayer={props.zIndexLayer}>
      <MarkerDiv borderWidth={0} fontSize="12px" size={35} {...props}>
        <HexagonMarker/>
        {props.value}
      </MarkerDiv>
    </MarkerWrapper>
  );
}

function BomMarker(props) {
  return (
    <MarkerWrapper zIndexLayer={props.zIndexLayer}>
      <MarkerDiv borderWidth={0} {...props}>
        <OctagonMarker/>
        {props.value}
      </MarkerDiv>
    </MarkerWrapper>
  );
}

function HilltopMarker(props) {
  return (
    <MarkerWrapper zIndexLayer={props.zIndexLayer}>
      <MarkerDiv borderWidth={0} size={42} svgTranslateY="-2px" {...props}>
        <PentagonMarker/>
        {props.value}
      </MarkerDiv>
    </MarkerWrapper>
  );
}

function NominatedMarker(props) {
  return (
    <MarkerWrapper zIndexLayer={props.zIndexLayer} nominated>
      <MarkerDivNominatedOuter>
        <MarkerDiv {...props} nominated>
          {props.value}
        </MarkerDiv>
      </MarkerDivNominatedOuter>
    </MarkerWrapper>
  );
}

function WindDirectionMarker(props) {
  const { windDirection } = props;
  return (
    <MarkerWrapper zIndexLayer={props.zIndexLayer}>
      <MarkerDiv {...props} borderWidth={0} size={50} smallWindArrow={false}>
        <ArrowMarker rotation={windDirection}/>
      </MarkerDiv>
    </MarkerWrapper>
  );
}

function DefaultMarker(props) {
  return (
    <MarkerWrapper zIndexLayer={props.zIndexLayer}>
      <MarkerDiv {...props}>
        {props.value}
      </MarkerDiv>
    </MarkerWrapper>
  );
}

const Marker = ({
                  siteName,
                  metricKey,
                  metricHash,
                  awsType,
                  windDirection,
                  nominated = false,
                  onClick,
                  onMouseOver,
                  onMouseOut,
                  divRef,
                  showWindArrows,
                }) => {
  const bgColor = metricHash?.styles?.background_color;
  const fontColor = metricHash?.styles?.font_color;
  let value = metricHash?.value;

  // Override value for unsupported metrics on non-standard site types
  if (metricHash?.unsupported) {
    if (awsType === 'bom') value = 'B';
    if (awsType === 'hilltop') value = 'H';
  }

  let windArrowImg = typeof windDirection === "number" ? WindArrow : null;

  // COTL-201: special wind direction logic for greenbrain sites.
  // only show wind direction when wind or GHCoP metrics are selected.
  if (
    awsType === 'greenbrain' &&
    !['ghcop', 'wind_speed_2m', 'wind_speed_10m'].includes(metricKey)
  ) {
    windDirection = null;
    windArrowImg = null;
  }

  const zIndexLayer = computeZIndex(awsType);
  const markerProps = {
    siteName,
    value,
    zIndexLayer,
    ref: divRef,
    onClick,
    onMouseOver: isBrowser ? onMouseOver : undefined,
    onMouseOut: isBrowser ? onMouseOut : undefined,
    windArrowImg,
    windDirection,
    bgColor,
    ...computeStyles({ awsType, bgColor, fontColor, windDirection }),
  };

  // console.log('markerProps:', {
  //   ...markerProps,
  //   ref: '[ref omitted]',
  // });

  if (mapWindSpeedArrowMode({ showWindArrows, metricKey })) {
    const windProps = {...markerProps, borderColor: null, backgroundColor: null, strokeWidth: 2 };
    return <WindDirectionMarker {...windProps} />;
  }
  if (awsType === 'greenbrain') return <GreenbrainMarker {...markerProps} />;
  if (awsType === 'bom') return <BomMarker {...markerProps} />;
  if (awsType === 'hilltop') return <HilltopMarker {...markerProps} />;
  if (nominated) return <NominatedMarker {...markerProps} />;
  return <DefaultMarker {...markerProps} />;
};

export default class MapMarker extends React.Component {

  constructor(props) {
    super(props)
    this.statusPopup = React.createRef()
    this.markerDiv = React.createRef()
  }

  componentDidUpdate(prevProps) {
  }

  getStatusPopupBoundingClientRect = () => {
    if (!this.statusPopup.current) return null
    return this.statusPopup.current.getBoundingClientRect()
  }

  getCenterPosition = () => {

  }

  renderBody = ({
                  value,
                  metricKey,
                  metricHash,
                  showSiteNameHoverPopup,
                  clickHandler,
                  mouseOverHandler,
                  mouseOutHandler,
                  name,
                  slug,
                  location,
                  statusHash,
                  siteSummaryDataHash,
                  awsType,
                  lastUpdated,
                  timezone,
                  nominated = false,
                  statusPopupCloseIconClickHandler,
                  routeToDataViewHandler,
                  isSignedIn,
                  showWindArrows
                }) => {
    let windDirection = siteSummaryDataHash && siteSummaryDataHash.wind_direction
    if (siteSummaryDataHash) {
      if (metricKey === 'wind_speed_2m') windDirection = siteSummaryDataHash.wind_direction_2m
      if (metricKey === 'wind_speed_10m') windDirection = siteSummaryDataHash.wind_direction_10m
      if (metricKey === 'ghcop') windDirection = siteSummaryDataHash.wind_direction_ghcop
    }
    const dataIsFresh = isDataFresh(awsType, lastUpdated, isSignedIn ? null : 3600000 * 3.25)

    // COTL-203: Hide marker from the map if we don't have this metric and the site is 'low quality'
    // (value 'X' denotes an unsupported metric for a low quality site)
    if (metricHash['unsupported'] && metricHash['value'] === 'X') {
      return null;
    }

    if (!dataIsFresh) {
      windDirection = null;
      metricHash['styles'] = {
        background_color: "rgba(255, 255, 255, 0.5)",
        font_color: "rgba(0, 0, 0, 0.3)",
        font_weight: "normal",
      }
      metricHash['value'] = "–";
      siteSummaryDataHash = { ...siteSummaryDataHash, ...{ data_array: siteSummaryDataHash.data_array.map((o) => ({ ...o, ...{ value_str: "–" } })) } }
    }

    // Hide marker from the map if we're in wind arrows mode, and wind directly is unknown
    if (!windDirection && mapWindSpeedArrowMode({ showWindArrows, metricKey })) {
      return null;
    }

    return (
      <div>
        {(showSiteNameHoverPopup) && <SiteNameHoverPopup name={name}/>}
        <Marker
          key={`${slug}-${metricKey}`}
          metricKey={metricKey}
          metricHash={metricHash}
          siteSummaryDataHash={siteSummaryDataHash}
          awsType={awsType}
          windDirection={computeWindDirection(windDirection)}
          nominated={nominated}
          onClick={clickHandler}
          onMouseOver={mouseOverHandler}
          onMouseOut={mouseOutHandler}
          divRef={this.markerDiv}
          siteName={name}
          showWindArrows={showWindArrows}
        />
      </div>
    )
  }

  render() {
    return this.renderBody(this.props)
  }
}
